import uuid from 'uuid';

import JsonApi, {getBaseDomainUrl} from "../../api/jsonApi";
import {getCommonVisualizerType} from "../../config";
import {setMultiLoader} from "../../actions/actionFunctions";
import {createThumbnailsCanvas, hideGroupObject, toBase64} from "./aiFunctions";
import {
  setAILoading,
  setAIResults, setAIWaitingId,
  setLookAiImageOpened,
  setLookAiRenderFinished,
  setLookAiWaitingBoxClicked,
  setDefaultAiFeatureAreasGroups,
} from "./aiActions";
import {LOOK_OVERLAYS} from "../look/core/constants";
import {getImagePromiseFromLayout} from "../../misc/getCanvasImageFromLayout";
import {getCurrentLayout} from "../appLayout";
import {convertImgToBase64URLNoCors} from "../../misc/utils";
import {playAudio} from "../../misc/audioCache";
import {lowerCaseFirstLetterKeys} from "../../misc/misc2";
import {attachResumeListenerOnce, detachResumeListener} from "./aiListenersLogic";

const newSwaggerHeaders = {
  'Accept': '*',
  'Content-Type': 'application/json'
}

const successSoundUrl = "https://qc-cdn.s3.dualstack.us-east-1.amazonaws.com/sounds/finish_tiered_chime.wav";

export const getCurrentPrompts = async () => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/Prompt`);
  const convertResponse = await rawResponse.json();
  console.log("convertResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  const visualizerType = getCommonVisualizerType();
  const filteredResponse = convertResponse.filter(prObject => prObject.tool === visualizerType);
  let firstActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "First"));
  let secondActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type === "Second"));
  let thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.isDefault && (prObject.type == "Third"));
  if (firstActiveIndex === -1) {
    firstActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "First"); // set at least something active
  }
  if (secondActiveIndex === -1) {
    secondActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Second"); // set at least something active
  }
  if (thirdActiveIndex === -1) {
    thirdActiveIndex = filteredResponse.findIndex(prObject => prObject.type === "Third"); // set at least something active
  }
  if (firstActiveIndex > -1) {
    filteredResponse[firstActiveIndex]["isActiveFirst"] = true;
  }
  if (secondActiveIndex > -1) {
    filteredResponse[secondActiveIndex]["isActiveSecond"] = true;
  }
  if (thirdActiveIndex > -1) {
    filteredResponse[thirdActiveIndex]["isActiveThird"] = true;
  }
  return filteredResponse
}

export const removePrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt/${pr.id}`, {
    method: 'DELETE',
  });
}

export const editPrompt = (pr) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'PATCH',
    headers: newSwaggerHeaders,
    body: JSON.stringify(pr)
  });
}

export const createPrompt = (postData) => {
  return fetch(`${getBaseDomainUrl()}:7122/api/Prompt`, {
    method: 'POST',
    headers: newSwaggerHeaders,
    body: JSON.stringify(postData)
  });
}

export const setDefaultPrompts = async (activePromptFirst, activePromptSecond, activePromptThird) => {
  if (activePromptFirst) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptFirst.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptSecond) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptSecond.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
  if (activePromptThird) {
    await fetch(`${getBaseDomainUrl()}:7122/api/Prompt/default/${activePromptThird.id}`, {
      method: 'PATCH',
      headers: newSwaggerHeaders,
    })
  }
}

const fixAiResponse = (obj) => {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return obj;
  }
  const newObj = lowerCaseFirstLetterKeys(obj);
  if (Array.isArray(newObj.files) && newObj.files.length) {
    newObj.files = newObj.files.map(lowerCaseFirstLetterKeys);
  }
  return newObj
}

const addCanvasImageToPayload = async (onlyRenderGroups = null,
                                       sendAsPNG = false,
                                       cutImageByItsSides = true) => {
  const imageData = await getImagePromiseFromLayout(
    getCurrentLayout(),
    {width: 1920, height: 1080},
    1,
    false,
    onlyRenderGroups, // onlyRenderGroups.map(fg => fg.featureGroup) // no need for that as we are using only partId
    sendAsPNG, // png with a transparent background
    cutImageByItsSides,
  );
  return {
    "b64File": imageData.split(',')[1],
    "fileName": sendAsPNG ? "reference.png" : "reference.jpg"
  }
}

// builds the "featureAreas" payload array from the groups selected in UiPopupAIContentFeaturesAreas.
// Refreshes the default groups right before building the payload (setDefaultAiFeatureAreasGroups
// no-ops on its own once the user has directly edited aiFeatureAreas), so this is the one place
// that guarantees the payload reflects whatever is currently on the canvas.
const _prepareFeatureAreasPayload = async (dispatch, getState) => {
  dispatch(setDefaultAiFeatureAreasGroups());
  const {aiFeatureAreas, aiRenderOptions} = getState().aiState;
  const {aiFeatureAreaEnabled, aiSendAsPNG} = aiRenderOptions;
  const featureAreasPayload = [];
  if (!aiFeatureAreaEnabled || !aiFeatureAreas) {
    return featureAreasPayload;
  }
  const layout = getCurrentLayout();
  for (const featureArea of Object.keys(aiFeatureAreas)) { // "Wet Area" / "Vanity Area"
    const partIds = aiFeatureAreas[featureArea];
    if (!Array.isArray(partIds) || !partIds.length) { // attach only if the area is selected
      continue;
    }
    const featureGroups = partIds
      .map(partId => layout.getFeatureGroupByPartId(partId))
      .filter(Boolean);
    if (!featureGroups.length) {
      continue;
    }
    const {b64File} = await addCanvasImageToPayload(featureGroups, aiSendAsPNG, true);
    featureAreasPayload.push({
      "featureArea": featureArea,
      "fileName": `${featureArea}.${aiSendAsPNG ? "png" : "jpg"}`,
      "b64File": b64File,
    });
  }
  return featureAreasPayload;
}

// builds the postData fields shared by postGenerateAiImage and _preparePostData
const createCommonPostData = async (userFile, onlySelectedGroups, dispatch, getState, jobId) => {
  const {aiEnableReferenceImage, aiEnableThumbnailImages, aiSendOnlySelectedGroupsAsAReference,
    selectedChatModel, customChatModel, selectedImageModel, customImageModel, aiSendAsPNG} = getState().aiState.aiRenderOptions;

  const featureDefinitionIDs = onlySelectedGroups
    .filter(groupObject => groupObject.featureGroup?.activeFeature?.dbFeature)
    .map(groupObject => {
      return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
    });

  // colorOnly groups are a subset of onlySelectedGroups, so any id pushed here is
  // guaranteed to also be present in featureDefinitionIDs above - the backend
  // ignores a colorOnly id silently if it isn't in featureDefinitionIds too.
  const colorOnlyFeatureDefinitionIds = onlySelectedGroups
    .filter(groupObject => groupObject.colorOnly && groupObject.featureGroup?.activeFeature?.dbFeature)
    .map(groupObject => {
      return groupObject.featureGroup.activeFeature.dbFeature.featureDefinitionID
    });

  const base64Code = await toBase64(userFile);

  const postData = {
    "fabricatorId": document.kvConfig.userId,
    "featureDefinitionIds": featureDefinitionIDs,
    "colorOnlyFeatureDefinitionIds": colorOnlyFeatureDefinitionIds,
    "jobId": jobId,
    "userPhoto": {
      "b64File": base64Code,
      "fileName": userFile.name,
    },
    "tool": getCommonVisualizerType(),
    "skipImages": !aiEnableThumbnailImages,
    "openAI_CustomChatModel": selectedChatModel === "custom" ? customChatModel : selectedChatModel,
    "openAI_CustomImageModel": selectedImageModel === "custom" ? customImageModel : selectedImageModel,
    "featureAreas": await _prepareFeatureAreasPayload(dispatch, getState),
  };

  if (aiEnableReferenceImage) {
    const onlyGroups = aiSendOnlySelectedGroupsAsAReference ? onlySelectedGroups : null;
    postData.examplePhoto = await addCanvasImageToPayload(onlyGroups, aiSendAsPNG);
  }

  return postData;
}

export const postGenerateAiImage = (popupContext, signal) => {
  return async (dispatch, getState) => {
    const {groupObjects, userFile, promptsList} = popupContext;
    const storeState = getState();
    const {aiResults, aiRenderOptions} = storeState.aiState;
    const {aiSendOnlySelectedGroupsAsAReference, aiSendCombinedThumbnails} = aiRenderOptions;

    dispatch(setAILoading(true));
    dispatch(setMultiLoader({ activeStep: 0, visible: true, finished: false }));

    const onlySelectedGroups = groupObjects.filter(groupObject => {
      return groupObject.enabled && !hideGroupObject(groupObject) && groupObject.featureGroup?.activeFeature?.dbFeature?.featureDefinitionID
    });

    const activePromptFirst = promptsList.find(promptObject => promptObject.isActiveFirst);
    const activePromptSecond = promptsList.find(promptObject => promptObject.isActiveSecond);
    const activePromptThird = promptsList.find(promptObject => promptObject.isActiveThird);
    if (!activePromptFirst) {
      return
    }

    await setDefaultPrompts(activePromptFirst, activePromptSecond, activePromptThird);
    const jobId = document.kvConfig.jobId || storeState.serverState.jobId;

    const postData = await createCommonPostData(userFile, onlySelectedGroups, dispatch, getState, jobId);
    postData.firstPromptVersion = activePromptFirst.version;
    postData.secondPromptVersion = activePromptSecond ? activePromptSecond.version : 0;
    postData.thirdPromptVersion = activePromptThird ? activePromptThird.version : 0;

    if (aiSendCombinedThumbnails) {
      postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
    }

    console.log("postData", postData);
    try {
      const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
        method: 'POST',
        headers: newSwaggerHeaders,
        body: JSON.stringify(postData),
        signal,
      });
      const convertResponse = await rawResponse.json();
      await playAudio(successSoundUrl);
      dispatch(setAILoading(false));
      dispatch(setAIResults([...aiResults, fixAiResponse(convertResponse)]));
      console.log("convertResponse", convertResponse);
      return convertResponse ? convertResponse.resultImageUrl : "";
    } catch (e) {
      dispatch(setAILoading(false));
      return "";
    }
  }
}

const _getFeatureGroupsWithEnabledThumbnails = async () => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/Prompt`);
  const convertResponse = await rawResponse.json();
  if (!Array.isArray(convertResponse)) {
    return []
  }
  const visualizerType = getCommonVisualizerType();
  const filteredResponse = convertResponse.filter(prObject => prObject.tool === visualizerType);
  let firstActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "First"));
  let secondActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "Second"));
  let thirdActiveIndex = filteredResponse.find(prObject => prObject.isDefault && (prObject.type === "Third"));
  const activePrompts = [firstActiveIndex, secondActiveIndex, thirdActiveIndex].filter(prObject => {
    return prObject && (prObject.id || (typeof prObject.id === "number"))
  });
  if (!activePrompts.length) {
    return []
  }
  const allResponses = await Promise.all(
    activePrompts.map((prObject) => getCurrentPromptParts(prObject))
  );
  const layout = getCurrentLayout();
  const groupsWithEnabledThumbnails = allResponses.reduce((total, prPartArray) => {
    if (!Array.isArray(prPartArray) || !prPartArray.length) {
      return total;
    }
    prPartArray.forEach(partObject => {
      if (partObject.sendThumbnail !== false && !total.some(fg => (fg.dbName || fg.partName) === partObject.featureGroup)) {
        const fg = layout.getFeatureGroupByGroupName(partObject.featureGroup);
        if (fg) {
          total.push(fg);
        }
      }
    });
    return total;
  }, []);
  // console.log("groupsWithEnabledThumbnails", groupsWithEnabledThumbnails);
  return groupsWithEnabledThumbnails
}

const _preparePostData = async (lookAiPopupContext, storeState, dispatch, getState) => {
  const {userFile, groupObjects} = lookAiPopupContext;
  const {serverState, aiState} = storeState;

  const {aiSendOnlySelectedGroupsAsAReference, aiSendCombinedThumbnails} = aiState.aiRenderOptions;

  dispatch(setAILoading(true));

  if (serverState.isMobile) {
    const scrollDiv = document.querySelector(".look-ai__content");
    const loadingDiv = document.querySelector(".look-ai__sidebar-content--dropzone");
    if (loadingDiv && scrollDiv?.scrollTop > 120) {
      loadingDiv.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  const onlySelectedGroups = groupObjects.filter(groupObject => groupObject.enabled && !hideGroupObject(groupObject));

  // "firstPromptVersion" / "secondPromptVersion" are not passed here - we should use default ones
  const postData = await createCommonPostData(userFile, onlySelectedGroups, dispatch, getState, document.kvConfig.jobId || serverState.jobId);

  if (aiSendCombinedThumbnails) {
    try {
      const featureGroupsWithEnabledThumbnails = await _getFeatureGroupsWithEnabledThumbnails();
      if (Array.isArray(featureGroupsWithEnabledThumbnails) && featureGroupsWithEnabledThumbnails.length) {
        postData.productGridImage =  await createThumbnailsCanvas(
          onlySelectedGroups,
          aiSendOnlySelectedGroupsAsAReference,
          featureGroupsWithEnabledThumbnails
        );
      } else {
        postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
      }
    } catch (e) {
      postData.productGridImage =  await createThumbnailsCanvas(onlySelectedGroups, aiSendOnlySelectedGroupsAsAReference);
    }
  }
  return postData
}

// True once every request in the current generation batch has settled (see
// _isLastInGenerationBatch below). Only the LAST one to settle should trigger the
// one-time "we're fully done" effects - playing the success sound, clearing
// aiLoading, detaching the resume listener, and opening the waiting box/panel.
// Every settling request still gets its own aiResults slot, regardless of order.
const _isLastInGenerationBatch = (batchTracker) => {
  if (!batchTracker) return true; // no tracker (e.g. the resume listener) => solo request
  batchTracker.pending -= 1;
  return batchTracker.pending <= 0;
}

// Writes `entry` into `existingResults` at a fixed, predetermined slot index -
// used when this result's position was reserved up front (see
// startAIImageGeneration), so two concurrent requests can never race over "the
// next empty slot": whichever settles first still lands in ITS OWN reserved
// slot, never the other's. Pads with empty placeholder slots if the array
// isn't long enough yet (e.g. this is index N+1 but the sibling hasn't
// appended its own index-N slot yet).
const _placeResultAtIndex = (existingResults, index, entry) => {
  const next = existingResults.map(result => ({...result}));
  while (next.length <= index) {
    next.push({});
  }
  next[index] = entry;
  return next;
}

const _parseImageResponse = async (convertResponse, dispatch, getState, batchTracker, isPrimary = true, targetIndex = null) => {

  const isLastInBatch = _isLastInGenerationBatch(batchTracker);

  if (isLastInBatch) {
    detachResumeListener?.();
  }

  const storeState = getState();
  const {aiResults, aiUserFileBlobUrl} = storeState.aiState;
  const {lookOverlayOpened} = storeState.lookState;

  // Only the primary (non-multiImage) result of a batch takes over "isActive" -
  // the secondary multiImage result is inserted into its own slot without
  // touching anyone's isActive flag, so regardless of which of the two settles
  // first, the primary result ends up the active/displayed one.
  const nextAiResults = isPrimary
    ? aiResults.map(aiResult => Object.assign({}, aiResult, {isActive: false}))
    : aiResults.map(aiResult => Object.assign({}, aiResult));

  convertResponse.id = uuid.v4();
  convertResponse.isActive = isPrimary;
  convertResponse.sourceImageUrl = aiUserFileBlobUrl;

  if (targetIndex !== null) {
    dispatch(setAIResults(_placeResultAtIndex(nextAiResults, targetIndex, convertResponse)));
  } else if (nextAiResults.every(res => res.resultImageUrl)) {
    dispatch(setAIResults([...nextAiResults, convertResponse]));
  } else {
    const nextUnrenderedIndex = nextAiResults.findIndex(res => !res.resultImageUrl);
    if (nextUnrenderedIndex > -1) {
      nextAiResults[nextUnrenderedIndex] = convertResponse;
      dispatch(setAIResults([...nextAiResults]));
    }
  }

  if (isLastInBatch) {
    await playAudio(successSoundUrl);
    dispatch(setAILoading(false));
    dispatch(setLookAiWaitingBoxClicked(true));
    if (lookOverlayOpened === LOOK_OVERLAYS.aiPanel) {
      dispatch(setLookAiImageOpened(true))
    } else {
      dispatch(setLookAiRenderFinished(true))
    }
  }

  console.log("convertResponse", convertResponse);
  return convertResponse ? convertResponse.resultImageUrl : "";
}

const _parseErrorResponse = async (errOrResponse, dispatch, getState, batchTracker, isPrimary = true, targetIndex = null) => {

  const isLastInBatch = _isLastInGenerationBatch(batchTracker);

  if (isLastInBatch) {
    detachResumeListener?.();
  }

  let message = "Failed to generate image. Please try again later";

  // HTTP error case
  if (errOrResponse && typeof errOrResponse === "object" && "ok" in errOrResponse && "status" in errOrResponse) {
    try {
      const contentType = errOrResponse.headers?.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await errOrResponse.json();
        message = data?.message || JSON.stringify(data);
      } else {
        message = (await errOrResponse.text()) || message;
      }
    } catch {
      // keep fallback message
    }
    message = `HTTP ${errOrResponse.status}: ${message}`;
  } else {
    // thrown error case
    message = errOrResponse?.message || String(errOrResponse) || message;
    if (message === "signal is aborted without reason") message = "Aborted manually";
  }

  const storeState = getState();
  const { aiResults } = storeState.aiState;

  // Same isActive rule as _parseImageResponse: only the primary request's outcome
  // (success or error) takes over isActive; a secondary multiImage error is just
  // inserted into its own slot.
  const nextAiResults = isPrimary
    ? aiResults.map(aiResult => ({ ...aiResult, isActive: false }))
    : aiResults.map(aiResult => ({ ...aiResult }));

  const convertResponse = {
    id: uuid.v4(),
    isActive: isPrimary,
    conversionError: message,
  };

  if (targetIndex !== null) {
    dispatch(setAIResults(_placeResultAtIndex(nextAiResults, targetIndex, convertResponse)));
  } else if (nextAiResults.every(res => res.resultImageUrl)) {
    dispatch(setAIResults([...nextAiResults, convertResponse]));
  } else {
    const idx = nextAiResults.findIndex(res => !res.resultImageUrl);
    if (idx > -1) {
      nextAiResults[idx] = convertResponse;
      dispatch(setAIResults([...nextAiResults]));
    }
  }

  if (isLastInBatch) {
    dispatch(setAILoading(false));
    dispatch(setLookAiImageOpened(false));
    dispatch(setLookAiWaitingBoxClicked(true));
  }
};

// Funnels every error-path settlement (immediate HTTP error, poll HTTP error,
// Failed/Interrupted status, thrown/aborted exception) through one place so a
// batch of two requests (colorOnly) only ever reports ONE error. When part of
// a batch (batchErrorCoordinator set): the first request to fail claims the
// error - it gets a real _parseErrorResponse call (aiResults entry + isActive),
// and claiming also aborts the AbortController shared by both requests, which
// makes the sibling's own in-flight fetch/poll reject too. That rejection
// lands here as well, sees the error was already claimed, and just settles the
// batch tracker quietly (still firing the "last in batch" finish effects once
// truly both are done) instead of reporting a second, redundant failure. A
// solo request (no batchErrorCoordinator) always reports for real, unchanged.
const _reportBatchError = async (errPayload, dispatch, getState, batchTracker, isPrimary, targetIndex, batchErrorCoordinator) => {
  if (batchErrorCoordinator) {
    if (batchErrorCoordinator.alreadyClaimed()) {
      const isLastInBatch = _isLastInGenerationBatch(batchTracker);
      if (isLastInBatch) {
        detachResumeListener?.();
        dispatch(setAILoading(false));
        dispatch(setLookAiImageOpened(false));
        dispatch(setLookAiWaitingBoxClicked(true));
      }
      return "";
    }
    batchErrorCoordinator.claim();
  }
  await _parseErrorResponse(errPayload, dispatch, getState, batchTracker, isPrimary, targetIndex);
  return "";
};

export const postGenerateLookAiImage = (lookAiPopupContext, signal) => {

  return startAIImageGeneration(lookAiPopupContext, signal)

  // return async (dispatch, getState) => {
  //
  //   const postData = await _preparePostData(lookAiPopupContext, getState(), dispatch);
  //
  //   console.log("postData", postData);
  //   try {
  //     const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/convert`, {
  //       method: 'POST',
  //       headers: newSwaggerHeaders,
  //       body: JSON.stringify(postData),
  //       signal,
  //     });
  //
  //     if (!rawResponse.ok) {
  //       await _parseErrorResponse(rawResponse, dispatch, getState); // ✅ centralized
  //       return "";
  //     }
  //     const convertResponse = await rawResponse.json();
  //     return await _parseImageResponse(convertResponse, dispatch, getState);
  //   } catch (e) {
  //     // await new Promise(resolve => setTimeout(resolve, 5000)); // devtest // need to change api url to get here
  //     await _parseErrorResponse(e, dispatch, getState);
  //     return "";
  //   }
  // }
}

// const sendImageToOpenAI = async (base64Image) => {
//   try {
//     // Convert base64 to blob
//     const byteString = atob(base64Image.split(',')[1]);
//     const mimeString = base64Image.split(',')[0].split(':')[1].split(';')[0];
//     const ab = new ArrayBuffer(byteString.length);
//     const ia = new Uint8Array(ab);
//     for (let i = 0; i < byteString.length; i++) {
//       ia[i] = byteString.charCodeAt(i);
//     }
//     const blob = new Blob([ab], { type: mimeString });
//
//     const file = new File([blob], 'image.png', { type: mimeString });
//
//     // Create form data
//     const formData = new FormData();
//     formData.append('image', file);
//     formData.append('n', '1'); // number of images to return
//     formData.append('size', '512x512'); // size can be 256x256, 512x512, 1024x1024
//
//     // Send to OpenAI API (proxy this through your backend in production!)
//     const response = await axios.post(
//       'https://api.openai.com/v1/images/variations',
//       formData,
//       {
//         headers: {
//           // 'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
//           'Content-Type': 'multipart/form-data',
//         },
//       }
//     );
//
//     return response.data.data[0].url; // Returns a URL to the new image
//   } catch (error) {
//     console.error('Error sending image to OpenAI:', error);
//     throw error;
//   }
// };

export const getCurrentPromptParts = async (promptObject) => {
  const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/prompt/${promptObject.id}`);
  const convertResponse = await rawResponse.json();
  console.log("promptPartsResponse", convertResponse);
  if (!Array.isArray(convertResponse)) {
    return []
  }
  // return the whole array, removed filter by partBody text, since we need other values
  return convertResponse
}

export const updatePromptParts = (groupObject, promptObject, type) => {
  const isActiveBody = "partBody" + type;
  const savedPromptPartObject = "savedPromptPartObject" + type;

  // console.log("groupObject", groupObject, promptObject, type);
  if (groupObject[savedPromptPartObject]) { // update
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/${groupObject[savedPromptPartObject].id}`, {
      method: 'PUT',
      headers: newSwaggerHeaders,
      body: JSON.stringify(Object.assign({}, groupObject[savedPromptPartObject], {
        partBody: groupObject[isActiveBody],
        sendThumbnail: groupObject["sendThumbnail"],
        enabled: groupObject["enabled"],
      }))
    });
  } else { // create
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/list`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify([{
        "id": 0,
        "promptId": promptObject.id,
        "featureGroup": groupObject.featureGroup.dbName || groupObject.featureGroup.partName,
        "partBody": groupObject[isActiveBody] || "",
        "sendThumbnail": groupObject.sendThumbnail,
        "enabled": groupObject.enabled,
      }])
    });
  }
}

export const createPromptsForNewPartObject = async (newPromptObject, oldPromptObject) => {

  const prPartsResponse = await getCurrentPromptParts(oldPromptObject);

  const allResponses = await Promise.all(prPartsResponse.map((prPart) => {
    return fetch(`${getBaseDomainUrl()}:7122/api/PromptParts/list`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify([{
        "id": 0,
        "promptId": newPromptObject.id,
        "featureGroup": prPart.featureGroup,
        "partBody": prPart.partBody,
        "sendThumbnail": prPart.sendThumbnail,
      }])
    })
  }))
  console.log("allResponses", allResponses);
}

export const saveCreatedAIImage = async (jobId, aiState) => {
  try {
    if (jobId && aiState.aiResults[0] && !aiState.aiResults[0].conversionError && aiState.aiResults[0].resultImageUrl) {
      const sourceImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].sourceImageUrl);
      const resultImageUrl = await convertImgToBase64URLNoCors(aiState.aiResults[0].resultImageUrl);
      let postData = [];
      if (sourceImageUrl) { // just in case
        postData.push({
          "fileName": "user_photo.png",
          "base64Content": sourceImageUrl.split(',')[1],
          "documentType": "user_room_photo",
          "jobId": jobId
        })
      }
      if (resultImageUrl) { // just in case
        postData.push({
          "fileName": "generated_photo.png",
          "base64Content": resultImageUrl, // s3 link!
          "documentType": "ai_room_photo",
          "jobId": jobId
        })
      }
      await JsonApi.postSaveJobDocument(postData);
      // dispatch(setAIResults(aiState.aiResults.map((aiRes, aiResIndex) => {
      //   if (aiResIndex === 0) {
      //     return Object.assign({}, aiRes, {isSavedOnBackend: true});
      //   }
      //   return aiRes
      // })))
    }
  } catch (error) {
    console.log("saveCreatedAIImage error", error);
  }
  return Promise.resolve();
}

const POLL_INTERVAL_MS = 10000;

const delay = (ms, signal) => {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);

    if (!signal) return;

    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };

    if (signal.aborted) return onAbort();

    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Guards against duplicate polling/finalization of the same process.
// The primary loop (startAIImageGeneration) and the resume listener
// (attachResumeListenerOnce, fired on window focus/visibility change) can both
// poll the same processId concurrently. Without these guards, both loops would
// see status "Finished" and each dispatch setAIResults — producing two images.
const activePollProcessIds = new Set(); // processIds currently being polled
let finalizedProcessId = null;          // last processId that produced a result/error

export const pollProgressUntilDone = async ({
                                        aiWaitingId,
                                        signal,
                                        dispatch,
                                        getState,
                                        batchTracker,
                                        isPrimary = true,
                                        targetIndex = null,
                                        batchErrorCoordinator,
                                      }) => {
  // If another loop is already polling this process, do not start a second one.
  if (activePollProcessIds.has(aiWaitingId)) return "";
  activePollProcessIds.add(aiWaitingId);

  try {
    while (true) {
      // stop immediately if user aborted
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

      const raw = await fetch(
        `${getBaseDomainUrl()}:7122/api/ImageProcessing/progress/${aiWaitingId}`,
        { method: "GET", headers: newSwaggerHeaders, signal }
      );

      if (!raw.ok) {
        // HTTP error while polling
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        return await _reportBatchError(raw, dispatch, getState, batchTracker, isPrimary, targetIndex, batchErrorCoordinator);
      }

      const progress = await raw.json();
      const status = progress?.status ?? progress?.Status;

      // If your API returns numeric enum, you might need mapping:
      // 0 Started, 1 InProgress, 2 Failed, 3 Finished, 4 Interupted

      if ((status === "Finished" || status === 3) && progress.response) {
        // Finalize exactly once per process (check+set is synchronous, so even
        // concurrent loops can only pass here a single time).
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        return await _parseImageResponse(fixAiResponse(progress.response), dispatch, getState, batchTracker, isPrimary, targetIndex);
      }

      if (status === "Failed" || status === 2 || status === "Interupted" || status === 4) {
        if (finalizedProcessId === aiWaitingId) return "";
        finalizedProcessId = aiWaitingId;
        // Build an Error-like object for your error handler
        const msg =
          progress?.response?.error ||
          progress?.message ||
          progress?.error ||
          progress?.conversionError ||
          `AI processing ${status === 4 || status === "Interupted" ? "was interrupted" : "failed"}`;

        return await _reportBatchError({ message: msg }, dispatch, getState, batchTracker, isPrimary, targetIndex, batchErrorCoordinator);
      }

      // Started / InProgress => wait and poll again
      await delay(POLL_INTERVAL_MS, signal);
    }
  } finally {
    activePollProcessIds.delete(aiWaitingId);
  }
};

// Posts a single start-convert request and polls it through to completion/error.
// Shared by the primary generation request and, when the fabricator has colorOnly
// AI enabled, the additional multi-image request fired alongside it - each gets
// its own processId and is polled/finalized independently, landing its own entry
// in aiResults via the existing pollProgressUntilDone -> _parseImageResponse /
// _parseErrorResponse pipeline (already keyed by processId, so this "just works"
// for two concurrent requests).
// trackAsWaitingId is only ever true for the primary request: aiState.aiWaitingId
// is a single slot used by the visibility/focus resume listener, so only one
// in-flight request can be resumed after a tab reload/refocus - the multi-image
// request is not resumable that way.
const _startAndPollAIImageGeneration = async ({postData, signal, dispatch, getState, trackAsWaitingId, batchTracker, targetIndex = null, batchErrorCoordinator}) => {
  console.log("postData", postData);
  try {
    const rawResponse = await fetch(`${getBaseDomainUrl()}:7122/api/ImageProcessing/start-convert`, {
      method: 'POST',
      headers: newSwaggerHeaders,
      body: JSON.stringify(postData),
      signal,
    });

    console.log("rawResponse11", rawResponse);

    if (!rawResponse.ok) {
      return await _reportBatchError(rawResponse, dispatch, getState, batchTracker, trackAsWaitingId, targetIndex, batchErrorCoordinator); // ✅ centralized
    }

    const data = await rawResponse.json();
    const processId = data?.processId;

    if (!processId) {
      return await _reportBatchError(rawResponse, dispatch, getState, batchTracker, trackAsWaitingId, targetIndex, batchErrorCoordinator); // ✅ centralized
    }

    if (trackAsWaitingId) {
      dispatch(setAIWaitingId(processId));
      attachResumeListenerOnce(dispatch, getState, signal);
    }

    return await pollProgressUntilDone({ aiWaitingId: processId, signal, dispatch, getState, batchTracker, isPrimary: trackAsWaitingId, targetIndex, batchErrorCoordinator });
  } catch (e) {
    // await new Promise(resolve => setTimeout(resolve, 5000)); // devtest // need to change api url to get here
    return await _reportBatchError(e, dispatch, getState, batchTracker, trackAsWaitingId, targetIndex, batchErrorCoordinator);
  }
}

export const startAIImageGeneration = (lookAiPopupContext, signal) => {
  return async (dispatch, getState) => {
    const postData = await _preparePostData(lookAiPopupContext, getState(), dispatch, getState);

    const {aiSettings} = getState().fabricatorState;
    const runMultiImageToo = !!aiSettings?.colorOnly
      && Array.isArray(postData.colorOnlyFeatureDefinitionIds)
      && postData.colorOnlyFeatureDefinitionIds.length > 0;

    // Shared across both requests so the "fully finished" effects (success sound,
    // aiLoading=false, waiting box/panel) fire once - after the LAST request
    // settles - instead of once per request. See _isLastInGenerationBatch.
    const batchTracker = {pending: runMultiImageToo ? 2 : 1};

    if (!runMultiImageToo) {
      return await _startAndPollAIImageGeneration({postData, signal, dispatch, getState, trackAsWaitingId: true, batchTracker});
    }

    // Reserve each request's target aiResults slot up front - before either
    // request fires - so the two concurrent requests never race over "the next
    // empty slot": the primary result always lands at primaryIndex, and the
    // multiImage result always lands right after it at primaryIndex + 1,
    // regardless of which request's network call/poll actually settles first.
    const {aiResults: currentAiResults} = getState().aiState;
    const firstUnrenderedIndex = currentAiResults.findIndex(res => !res.resultImageUrl);
    const primaryIndex = firstUnrenderedIndex > -1 ? firstUnrenderedIndex : currentAiResults.length;
    const secondaryIndex = primaryIndex + 1;

    // Chain an internal AbortController off the caller's signal, shared by both
    // requests, so either an external cancel or a same-batch error (via
    // batchErrorCoordinator.claim(), below) stops both requests.
    const batchController = new AbortController();
    if (signal) {
      if (signal.aborted) batchController.abort();
      else signal.addEventListener("abort", () => batchController.abort(), {once: true});
    }

    // Coordinates "one error cancels the whole batch and only reports once":
    // the first request to fail claims the error (reported for real, see
    // _reportBatchError) and aborts batchController; the other request's
    // resulting rejection then sees the error was already claimed and settles
    // quietly instead of reporting a second, redundant failure.
    let batchErrorClaimed = false;
    const batchErrorCoordinator = {
      alreadyClaimed: () => batchErrorClaimed,
      claim: () => {
        if (batchErrorClaimed) return false;
        batchErrorClaimed = true;
        batchController.abort();
        return true;
      },
    };

    const multiImagePostData = {...postData, multiImage: true};

    const [primaryResult] = await Promise.all([
      _startAndPollAIImageGeneration({
        postData, signal: batchController.signal, dispatch, getState,
        trackAsWaitingId: true, batchTracker, targetIndex: primaryIndex, batchErrorCoordinator,
      }),
      _startAndPollAIImageGeneration({
        postData: multiImagePostData, signal: batchController.signal, dispatch, getState,
        trackAsWaitingId: false, batchTracker, targetIndex: secondaryIndex, batchErrorCoordinator,
      }),
    ]);

    return primaryResult;
  }
}
